import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { ACCENT, api, FRAMEWORK_META } from "@/lib/api";
import { DINACHARYA_DEMO } from "@/lib/demoScores";
import { useDinacharyaScores } from "@/lib/dinacharyaScores";

const INSIDE = {
  dinacharya: {
    headline: "The daily and monthly rhythm of accountability",
    footer: "Team score, score bands and the review deck — the discipline that runs the business.",
    cards: [
      { eyebrow: "Review deck", title: "Daily review tracker", body: "Track daily reviews and discipline across the leadership team." },
      { eyebrow: "Review deck", title: "Monthly planner", body: "Plan budgets and targets for the month ahead." },
      { eyebrow: "Review deck", title: "Monthly review tracker", body: "Close the month with a scored performance review." },
    ],
  },
  panchatanthra: {
    headline: "Five business elements, one business score",
    footer: "Each element drills down into its survey, monthly summary, commitments and weekly review.",
    cards: [],
  },
  viveka_kartha: {
    headline: "Three family pillars, one legacy score",
    footer: "Family charter versions, wealth buckets and succession commitments in one legacy system.",
    cards: [
      { eyebrow: "Pillar", title: "Family charter", body: "Define family values, vision and purpose — versioned and previewable." },
      { eyebrow: "Pillar", title: "Wealth allocation", body: "Align wealth across safety, market and aspirational buckets." },
      { eyebrow: "Pillar", title: "Succession & Kartha", body: "Plan leadership continuity and Kartha accountability." },
    ],
  },
};

/** Full-bleed placard slides for presenting the Family Business 360 framework on a big screen. */
export default function Showcase() {
  const { tenantId, access } = useAuth();
  const { scores: dcScores } = useDinacharyaScores();
  const [scorecard, setScorecard] = useState({});
  const [ptElements, setPtElements] = useState([]);
  const [index, setIndex] = useState(0);
  const frameworks = access?.frameworks || [];

  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/scorecard`).then(({ data }) => setScorecard(data)).catch(() => {});
    if (frameworks.includes("panchatanthra")) {
      api.get(`/tenants/${tenantId}/panchatanthra/overview`)
        .then(({ data }) => setPtElements((data.elements || []).map((e) => ({ ...e, eyebrow: "Element" }))))
        .catch(() => {});
    }
  }, [tenantId, frameworks]);

  const slides = [
    { kind: "intro" },
    ...frameworks.flatMap((fw) => [{ kind: "framework", fw }, { kind: "inside", fw }]),
  ];
  const total = slides.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIndex((i) => Math.min(total - 1, i + 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total]);

  const slide = slides[index];
  const meta = slide?.fw ? FRAMEWORK_META[slide.fw] : null;
  const rawScore = slide?.fw ? scorecard.frameworks?.[slide.fw] : null;
  const score = slide?.fw !== "dinacharya" ? rawScore
    : dcScores?.team_score != null
      ? { ...rawScore, score10: dcScores.team_score, band: dcScores.team_band }
      : rawScore?.score10 == null ? { ...rawScore, ...DINACHARYA_DEMO } : rawScore;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-navy" data-testid="showcase">
      <div className="absolute inset-0"
           style={{ background:
             "linear-gradient(120deg, hsl(233 44% 7% / 0.94), hsl(234 44% 11% / 0.72))," +
             "radial-gradient(56rem 36rem at 88% 10%, hsl(186 74% 38% / 0.2), transparent 62%)," +
             "radial-gradient(46rem 30rem at 6% 92%, hsl(288 52% 50% / 0.2), transparent 62%)" }} />
      {meta && (
        <div key={meta.name}
             className="absolute right-[7%] top-1/2 hidden h-[40vh] w-[40vh] -translate-y-1/2 animate-[rise_1.2s_cubic-bezier(0.22,1,0.36,1)_both] place-items-center rounded-full bg-white/95 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.7)] lg:grid">
          <img src={meta.icon} alt="" className="h-[27vh] w-[27vh] object-contain" />
        </div>
      )}
      <div className="grain-dark relative z-10 flex h-full flex-col justify-between px-10 py-10 text-white md:px-20 md:py-14">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold uppercase tracking-[0.34em] text-gold">
            Aikyam · Family Business 360°
          </p>
          <Link to="/" data-testid="showcase-exit"
                className="rounded-full border border-white/25 p-2.5 text-white/70 transition-colors hover:border-gold hover:text-gold">
            <X size={16} />
          </Link>
        </div>

        {slide?.kind === "intro" ? (
          <div className="max-w-4xl" data-testid="showcase-intro">
            <h1 className="font-display text-6xl leading-[1.02] md:text-8xl">
              Three perspectives.
              <br />
              <span className="text-gold">One purpose.</span>
            </h1>
            <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
              {frameworks.map((fw) => {
                const m = FRAMEWORK_META[fw];
                const a = ACCENT[m.accent];
                return (
                  <div key={fw} className="border-t border-white/15 pt-5">
                    <span className={`block h-[3px] w-12 rounded-full ${a.bar}`} />
                    <p className="mt-4 text-[9px] font-semibold uppercase tracking-[0.26em] text-white/50">{m.lens}</p>
                    <p className="font-display text-2xl">{m.name}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">{m.question}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : slide?.kind === "inside" ? (
          <div className="w-full" data-testid={`showcase-inside-${slide.fw}`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-gold">
              Inside {meta?.name}
            </p>
            <h1 className="mt-3 font-display text-4xl leading-tight md:text-5xl">
              {INSIDE[slide.fw].headline}
            </h1>
            <div className="mt-10 grid max-w-5xl grid-cols-1 gap-5 md:grid-cols-3">
              {(slide.fw === "panchatanthra" ? ptElements : INSIDE[slide.fw].cards).map((row, i) => (
                <div key={row.name || row.title || i}
                     className="glass-dark rounded-2xl px-6 py-5">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-white/45">
                    {row.eyebrow || "Element"}
                  </p>
                  <p className="mt-2 font-display text-2xl">{row.name || row.title}</p>
                  {row.score10 !== undefined ? (
                    <p className="mt-3 font-mono text-4xl tabular-nums text-gold">
                      {row.score10 ?? "—"}<span className="text-base text-white/35">/10</span>
                    </p>
                  ) : (
                    <p className="mt-3 text-sm leading-relaxed text-white/60">{row.body}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-9 flex items-end gap-10">
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-white/45">
                  {meta?.scoreLabel}
                </p>
                <p className="mt-1 font-mono text-6xl tabular-nums text-gold">
                  {score?.score10 ?? "—"}<span className="text-xl text-white/35">/10</span>
                </p>
              </div>
              <p className="pb-3 max-w-md text-sm leading-relaxed text-white/55">
                {INSIDE[slide.fw].footer}
              </p>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl" data-testid={`showcase-slide-${slide?.fw}`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-gold">{meta?.lens}</p>
            <h1 className="mt-4 font-display text-6xl leading-[1.02] md:text-8xl">{meta?.name}</h1>
            <p className="mt-7 max-w-2xl text-xl italic leading-relaxed text-white/80 md:text-2xl">
              “{meta?.question}”
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/60">{meta?.tagline}</p>
            <div className="mt-12 flex items-end gap-12">
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-white/45">Latest score</p>
                <p className="mt-1 font-mono text-7xl tabular-nums text-gold"
                   data-testid={`showcase-score-${slide?.fw}`}>
                  {score?.score10 ?? "—"}<span className="text-2xl text-white/35">/10</span>
                </p>
              </div>
              <div className="pb-3">
                <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-white/45">Maturity</p>
                <p className="mt-1 font-display text-3xl">{score?.band || "Not scored"}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-white/35">
            Scalable · Saleable · Transferable
          </p>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-white/45" data-testid="showcase-counter">{index + 1} / {total}</span>
            <button data-testid="showcase-prev" disabled={index === 0}
                    onClick={() => setIndex((i) => Math.max(0, i - 1))}
                    className="rounded-full border border-white/25 p-2.5 transition-colors hover:border-gold hover:text-gold disabled:opacity-30">
              <ArrowLeft size={16} />
            </button>
            <button data-testid="showcase-next" disabled={index === total - 1}
                    onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
                    className="rounded-full border border-white/25 p-2.5 transition-colors hover:border-gold hover:text-gold disabled:opacity-30">
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
